// src/errors/monitoring/error-reporter.ts

/**
 * Error Reporter
 *
 * WHY: Send errors to external monitoring service
 * WHAT: Integration with Sentry (or similar)
 * BENEFIT:
 *   - Track errors in production
 *   - Alerting for critical issues
 *   - Error analytics and trends
 *
 * DESIGN DECISIONS:
 * 1. Lazy initialization
 * 2. Environment-aware (only active in production)
 * 3. Filters out non-reportable errors
 * 4. Adds context for debugging
 *
 * NOTE: This file uses Sentry as an example.
 * Replace with your preferred error monitoring service.
 */

import * as Sentry from "@sentry/react";
import { AppError, isAppError } from "../classes";
import { ErrorSeverity } from "../types";
import { envConfig, errorConfig } from "@/config";

/**
 * User context for error reporting
 */
interface UserContext {
  id: string;
  email?: string;
  username?: string;
}

/**
 * Error Reporter Class
 *
 * Handles sending errors to external monitoring service.
 */
class ErrorReporter {
  private isInitialized = false;

  /**
   * Initialize error reporting
   * Should be called once at app startup
   */
  init(): void {
    if (this.isInitialized) return;
    if (!errorConfig.reporting.enabled) return;
    if (!envConfig.sentry.dsn) {
      console.warn("Sentry DSN not configured. Error reporting disabled.");
      return;
    }

    try {
      Sentry.init({
        dsn: envConfig.sentry.dsn,
        environment: envConfig.sentry.environment,
        release: envConfig.sentry.release,

        // Performance monitoring
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],

        // Sample rates
        tracesSampleRate: envConfig.isProduction ? 0.1 : 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,

        // Error filtering
        beforeSend: (event, hint) => {
          const error = hint.originalException;

          // Filter out non-reportable errors
          if (isAppError(error)) {
            // Don't report operational errors with low severity
            if (error.isOperational && error.severity === ErrorSeverity.LOW) {
              return null;
            }
          }

          return event;
        },

        // Ignore certain errors
        ignoreErrors: [
          // Browser extensions
          "ResizeObserver loop",
          // Common network errors
          "Network Error",
          "Failed to fetch",
          // Cancelled requests
          "canceled",
          "cancelled",
        ],
      });

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize Sentry:", error);
    }
  }

  /**
   * Capture and report an error
   */
  captureError(error: AppError): void {
    if (!this.isInitialized) return;

    const serialized = error.serialize();

    Sentry.withScope((scope) => {
      // Set error level based on severity
      scope.setLevel(this.mapSeverityToLevel(error.severity));

      // Set tags for filtering
      scope.setTags({
        errorCode: error.code,
        errorCategory: error.category,
        isRetryable: String(error.isRetryable),
        isOperational: String(error.isOperational),
      });

      // Set context
      if (error.context) {
        scope.setContext("errorContext", {
          component: error.context.component,
          action: error.context.action,
          url: error.context.url,
          method: error.context.method,
          requestId: error.context.requestId,
        });
      }

      // Set extra data
      scope.setExtra("serializedError", serialized);

      // Capture the exception
      Sentry.captureException(error.cause ?? error);
    });
  }

  /**
   * Set user context for error reports
   */
  setUser(user: UserContext | null): void {
    if (!this.isInitialized) return;
    Sentry.setUser(user);
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(breadcrumb: {
    category: string;
    message: string;
    level?: "info" | "warning" | "error";
    data?: Record<string, unknown>;
  }): void {
    if (!this.isInitialized) return;

    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level ?? "info",
      data: breadcrumb.data,
    });
  }

  /**
   * Map error severity to Sentry level
   */
  private mapSeverityToLevel(severity: ErrorSeverity): Sentry.SeverityLevel {
    switch (severity) {
      case ErrorSeverity.LOW:
        return "info";
      case ErrorSeverity.MEDIUM:
        return "warning";
      case ErrorSeverity.HIGH:
        return "error";
      case ErrorSeverity.CRITICAL:
        return "fatal";
      default:
        return "error";
    }
  }
}

/**
 * Singleton instance
 */
export const errorReporter = new ErrorReporter();
